import { Group, Vector3 } from "three";
import { Arcs, ArcsOptions } from "./Arcs";
import { ArcsConfig, DEFAULT_CONFIG } from "./types";

export interface ElectricArcsOptions extends ArcsOptions {
  /** Override spawn behavior */
  autoSpawn?: boolean;
}

/**
 * Electric arcs effect for the main scene.
 * Wraps the generic Arcs class with auto-spawning behavior based on
 * scene settlement, sine wave intensity, and orbit width.
 */
export class ElectricArcs extends Group {
  private arcs: Arcs;
  private config: ArcsConfig;

  // Auto-spawn state
  private lastArcTime = 0;
  private settlementTime = 0;
  private currentCenter = new Vector3(0, 0, 1500);
  private previousCenter = new Vector3(0, 0, 1500);
  private orbitWidth = 1200;
  private isMobile: boolean;

  // Menu fade
  private menuOpacity = 1;
  private menuFadeStartTime = 0;
  private menuFadeTarget = 1;

  constructor(options: ElectricArcsOptions = {}) {
    super();
    this.config = this.mergeConfig(options.config);
    this.isMobile =
      options.forceMobile ??
      options.mobileDetector?.() ??
      window.innerWidth < 768;
    this.arcs = new Arcs(options);
    // this.add(this.arcs);
    this.frustumCulled = false;
  }

  private mergeConfig(partial?: Partial<ArcsConfig>): ArcsConfig {
    if (!partial) return { ...DEFAULT_CONFIG };
    return {
      animation: { ...DEFAULT_CONFIG.animation, ...partial.animation },
      spawn: { ...DEFAULT_CONFIG.spawn, ...partial.spawn },
      style: { ...DEFAULT_CONFIG.style, ...partial.style },
    };
  }

  public setMenuOpen(isOpen: boolean, time: number): void {
    this.menuFadeTarget = isOpen ? 0 : 1;
    this.menuFadeStartTime = time;
  }

  public update(
    time: number,
    targetCenter: Vector3,
    scrollVelocity: number,
    cameraRight: Vector3,
    orbitWidth: number,
  ): void {
    const { spawn } = this.config;

    // Menu fade (mobile only)
    if (this.isMobile && this.menuFadeStartTime > 0) {
      const elapsed = time - this.menuFadeStartTime;
      this.menuOpacity =
        this.menuFadeTarget === 0
          ? Math.max(0, 1 - elapsed / 0.2)
          : elapsed < 0.5
            ? 0
            : Math.min((elapsed - 0.5) / 0.5, 1);
    } else {
      this.menuOpacity = 1;
    }
    this.arcs.setIntensity(this.menuOpacity);

    // Track movement
    const movement = targetCenter.distanceTo(this.previousCenter);
    this.previousCenter.copy(this.currentCenter);
    this.currentCenter.copy(targetCenter);
    this.orbitWidth = orbitWidth;

    // Settlement tracking
    this.settlementTime =
      movement < 10 && scrollVelocity < 5 ? this.settlementTime + 1 / 60 : 0;

    // Sine wave intensity
    const sineIntensity = Math.max(0, Math.sin(time * spawn.sineSpeed));

    // Spawn new arcs
    const timeSinceLastArc = time - this.lastArcTime;
    const [minInterval, maxInterval] = spawn.spawnInterval;
    if (
      this.settlementTime > spawn.settlementDelay &&
      sineIntensity > 0 &&
      timeSinceLastArc >
        minInterval + Math.random() * (maxInterval - minInterval) &&
      this.arcs.getArcCount() < spawn.maxArcs
    ) {
      const halfWidth = this.orbitWidth / 2;
      const widthFactor = Math.min(1, this.orbitWidth / 800);
      const isFullWidth = Math.random() < 0.75;

      let startOffset: number, endOffset: number, startY: number, endY: number;

      if (isFullWidth) {
        const inset = 30;
        const variance = halfWidth * 0.1;
        startOffset = -halfWidth + inset + (Math.random() - 0.5) * variance;
        endOffset = halfWidth - inset + (Math.random() - 0.5) * variance;
        if (Math.random() < 0.5) {
          [startOffset, endOffset] = [endOffset, startOffset];
        }

        const bottomOffset = (this.isMobile ? -25 : -60) * widthFactor;
        const yVariance = (this.isMobile ? 10 : 25) * widthFactor;
        startY = bottomOffset + Math.random() * yVariance;
        endY = bottomOffset + Math.random() * yVariance;
      } else {
        startOffset = (Math.random() - 0.5) * 2 * halfWidth;
        endOffset = (Math.random() - 0.5) * 2 * halfWidth;
        const range = (this.isMobile ? 80 : 200) * widthFactor;
        startY = (Math.random() - 0.5) * range;
        endY = (Math.random() - 0.5) * range;
      }

      const start = this.currentCenter
        .clone()
        .addScaledVector(cameraRight, startOffset)
        .add(new Vector3(0, startY, 0));
      const end = this.currentCenter
        .clone()
        .addScaledVector(cameraRight, endOffset)
        .add(new Vector3(0, endY, 0));

      this.arcs.spawnArc({
        startPoint: start,
        endPoint: end,
        intensity: sineIntensity * (0.6 + Math.random() * 0.4),
      });
      this.lastArcTime = time;
    }

    // Update existing arcs
    this.arcs.update();
  }

  public dispose(): void {
    this.arcs.dispose();
  }
}
